import { Dispatch, SetStateAction, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';
import ObjectSort from 'shared/models/object-sort';
import Pagination from 'shared/models/pagination';

export type TableSortInitializer<T extends object> = {
  sessionStorageKey: string;
  defaultSort: ObjectSort<T>;
};

export type TableFiltering<T extends object> = {
  currentPagination: [Pagination, Dispatch<SetStateAction<Pagination>>];
  currentSearchQuery: [string, Dispatch<SetStateAction<string>>];
  currentPage: number;
  currentRowsPerPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  setCurrentRowsPerPage: Dispatch<SetStateAction<number>>;
  handleOnRowsPerPageChanged: (rowsPerPage: number) => void;
  handleOnPageChanged: (page: number) => void;
  handleOnSearchChanged: (value: string) => void;
  handleOnSortChanged: (property: NestedKeyof<T>, isAscending: boolean) => void;
  resetPaging: () => void;
  sortFromSessionStorage: ObjectSort<T> | undefined;
};

const useTableFiltering = <T extends object>(tableSortInitializer: TableSortInitializer<T> | undefined = undefined): TableFiltering<T> => {
  const [currentPagination, setCurrentPagination] = useState<Pagination>({
    skip: 0,
    take: 50,
  });
  const [currentSearchQuery, setCurrentSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [currentRowsPerPage, setCurrentRowsPerPage] = useState(50);
  const [sortFromSessionStorage, setSortFromSessionStorage] = useSessionStorage<ObjectSort<T> | undefined>(
    tableSortInitializer?.sessionStorageKey ?? '',
    tableSortInitializer?.defaultSort ?? undefined,
  );

  const handleOnRowsPerPageChanged = (rowsPerPage: number): void => {
    setCurrentRowsPerPage(rowsPerPage);
    setCurrentPage(0);
    setCurrentPagination({
      skip: 0,
      take: rowsPerPage,
    });
  };

  const handleOnPageChanged = (page: number): void => {
    if (page !== currentPage) {
      setCurrentPage(page);
      setCurrentPagination({
        skip: page * currentRowsPerPage,
        take: currentRowsPerPage,
      });
    }
  };

  const handleOnSearchChanged = (value: string): void => {
    if (value !== currentSearchQuery) {
      setCurrentSearchQuery(value);
      setCurrentPage(0);
      handleOnPageChanged(0);
    }
  };

  const resetPaging = (): void => {
    setCurrentPage(0);
    handleOnPageChanged(0);
  };

  const handleOnSortChanged = (property: NestedKeyof<T>, isAscending: boolean): void => {
    setSortFromSessionStorage({
      isAscending,
      property: property as never,
    });

    setCurrentPage(0);
    setCurrentPagination({
      skip: 0,
      take: currentRowsPerPage,
    });
  };

  return {
    currentPagination: [currentPagination, setCurrentPagination],
    currentSearchQuery: [currentSearchQuery, setCurrentSearchQuery],
    currentPage,
    currentRowsPerPage,
    setCurrentPage,
    setCurrentRowsPerPage,
    handleOnRowsPerPageChanged: handleOnRowsPerPageChanged,
    handleOnPageChanged: handleOnPageChanged,
    handleOnSearchChanged: handleOnSearchChanged,
    handleOnSortChanged,
    resetPaging,
    sortFromSessionStorage,
  };
};

export default useTableFiltering;
